import React, { useId, useState } from "react";
import { searchPharmacies, pharmacyAddressLine } from "../../utils/medicalProfileApi";
import { useSubmitGuard } from "../../utils/useSubmitGuard";
import { errorMessage } from "../../utils/errorMessage";

const inputStyle = {
  height: "36px",
  width: "100%",
  borderRadius: "6px",
  border: "1px solid var(--youro-border)",
  backgroundColor: "var(--youro-card)",
  padding: "4px 12px",
  fontSize: "0.875rem",
  color: "var(--youro-charcoal)",
  boxSizing: "border-box",
};

const labelStyle = { fontSize: "0.875rem", fontWeight: 500, display: "block", marginBottom: "6px" };

/**
 * Finds a pharmacy in the national NPI directory by name and ZIP code (or city
 * and state) and hands the chosen one to onSelect. Used on the Profile and in the
 * intake form.
 *
 * The search button is the Sky secondary tier: on both screens the screen's own
 * Coral button (Save, Continue) is the primary action.
 */
const PharmacySearch = ({ onSelect, initialZip = "" }) => {
  const [name, setName] = useState("");
  const [zip, setZip] = useState(initialZip || "");
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [searching, guard] = useSubmitGuard();
  const nameId = useId();
  const zipId = useId();

  const search = guard(async () => {
    setError("");
    if (!/^\d{5}$/.test(zip.trim())) {
      setError("Enter your 5-digit ZIP code to search near you.");
      return;
    }
    try {
      setResults(await searchPharmacies({ name, zip }));
    } catch (err) {
      setResults(null);
      setError(errorMessage(err, "We couldn't search for pharmacies just now. Try again, or type your pharmacy's details instead."));
    }
  });

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      search();
    }
  };

  return (
    <div>
      <div className="pharmacy-search-row" style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: "12px", alignItems: "end" }}>
        <div>
          <label htmlFor={nameId} style={labelStyle}>Pharmacy name (optional)</label>
          <input id={nameId} type="text" value={name} placeholder="e.g. CVS, Walgreens"
                 onChange={(e) => setName(e.target.value)} onKeyDown={onKeyDown} style={inputStyle} />
        </div>
        <div>
          <label htmlFor={zipId} style={labelStyle}>ZIP code</label>
          <input id={zipId} type="text" inputMode="numeric" maxLength={5} value={zip}
                 onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))} onKeyDown={onKeyDown} style={inputStyle} />
        </div>
        <button type="button" onClick={search} disabled={searching} aria-busy={searching}
                style={{ height: "36px", padding: "0 16px", borderRadius: "6px", border: "1px solid var(--youro-sky)",
                         background: "transparent", color: "var(--youro-sky)", fontWeight: 600, fontSize: "0.875rem",
                         cursor: searching ? "default" : "pointer", opacity: searching ? 0.5 : 1 }}>
          {searching ? "Searching…" : "Search"}
        </button>
      </div>

      {error && <p role="alert" style={{ color: "var(--youro-error)", fontSize: "0.8125rem", margin: "8px 0 0" }}>{error}</p>}

      {results && results.length === 0 && (
        <p style={{ fontSize: "0.875rem", color: "var(--youro-muted)", margin: "12px 0 0" }}>
          No pharmacies found. Check the ZIP code, or try a shorter name.
        </p>
      )}

      {results && results.length > 0 && (
        <ul aria-label="Pharmacies found" style={{ listStyle: "none", padding: 0, margin: "12px 0 0", maxHeight: "260px", overflowY: "auto",
                    border: "1px solid var(--youro-border)", borderRadius: "8px" }}>
          {results.map((p) => (
            <li key={p.npi} style={{ borderBottom: "1px solid var(--youro-border)" }}>
              <button type="button" onClick={() => onSelect(p)}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--youro-mint)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                      style={{ width: "100%", textAlign: "left", padding: "10px 12px", border: "none", background: "transparent",
                               cursor: "pointer", color: "var(--youro-charcoal)" }}>
                <span style={{ display: "block", fontWeight: 600, fontSize: "0.875rem" }}>{p.name}</span>
                <span style={{ display: "block", fontSize: "0.8125rem", color: "var(--youro-muted)" }}>
                  {pharmacyAddressLine(p)}{p.phone ? ` · ${p.phone}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PharmacySearch;
